import { useMemo } from 'react';

import useToggle from '@lyearn/core/hooks/useToggle';
import { extractAvatars } from '@lyearn/core/utils/extractAvatars';
import { Trans, useTranslation } from '@lyearn/i18n';
import { EditOutline, InfoOutline } from '@lyearn/icons';
import { AddBlock } from '@lyearn/molecules';
import { cn } from '@lyearn/style';
import {
  AvatarGroupHorizontal,
  Box,
  LinkButton,
  Loader,
  Stack,
  Tooltip,
  Typography,
} from '@lyearn/ui';
import { tagsAndUserAutocompleteClasses } from '@/components/Form/components/Conditions/styles';
import useUsers from '@/components/UsersSearch/hooks/useUsers';
import { ReviewerConfigInput } from '@/types/schema';

import SelectReviewersDialog from '../SelectReviewersDialog';
import SelectUser from '../SelectUser';
import { dropdownContainerStyles } from '../SelectUser/styles';
import { validateReviewersError } from '../utils';

const LINK_TYPOGRAPHY = { variant: 'label-01' } as const;

function SelectReviewers({
  reviewerConfig,
  showError,
  onReviewerConfigChange,
  view,
}: {
  reviewerConfig: ReviewerConfigInput;
  showError?: boolean;
  onReviewerConfigChange: (nextValue: ReviewerConfigInput) => void;
  view?: 'modal' | 'form';
}) {
  const { t } = useTranslation();

  const [showDialog, { toggle }] = useToggle(false);

  const { data, isLoading } = useUsers({ ids: reviewerConfig.userIds ?? [] });

  const avatars = useMemo(() => extractAvatars(data), [data]);
  const error = validateReviewersError(reviewerConfig);

  const { type } = reviewerConfig;
  return (
    <>
      <Stack className="w-full" direction="col">
        <Stack alignItems="center">
          <Typography className="ms-8" variant="body-short-01">
            {t('Exercise:Creator.Reviewer')}
          </Typography>
          <Tooltip placement="right" title={t(`Exercise:InfoTooltip`)}>
            <Box className="ms-4">
              <InfoOutline className="h-18 w-18" fillColorToken="icon-secondary" />
            </Box>
          </Tooltip>
        </Stack>
        <Typography className="ms-8 mb-16" color="text-text-secondary" variant="body-short-02">
          {t(`Exercise:SelectReviewers`)}
        </Typography>
        {reviewerConfig.userIds?.length ? (
          <Stack alignItems="center" className="p-12 bg-bg-secondary rounded-8" gapX={8}>
            {isLoading ? (
              <Loader />
            ) : (
              <AvatarGroupHorizontal avatars={avatars} maxCount={4} size="x-small" />
            )}
            {reviewerConfig.userIds?.length > 1 ? (
              <Typography variant="body-long-01">
                <Trans
                  i18nKey="Exercise:ReviewersCount"
                  values={{ count: reviewerConfig.userIds?.length }}
                />
              </Typography>
            ) : null}
            <LinkButton
              className="ml-auto"
              startIcon={<EditOutline className="h-20 w-20" fillColorToken="link-internal" />}
              typography={LINK_TYPOGRAPHY}
              onClick={toggle}>
              {t('Common:Actions.Edit')}
            </LinkButton>
          </Stack>
        ) : (
          <>
            <AddBlock title={t(`Exercise:AddReviewrs`)} onClick={toggle} />
            {showError && error ? (
              <Typography className="ms-8 mt-8" color="text-text-error" variant="body-short-03">
                {error}
              </Typography>
            ) : null}
          </>
        )}
        {reviewerConfig.userIds && reviewerConfig.userIds?.length > 1 ? (
          <SelectUser
            className={view === 'form' ? cn('w-1/2 mt-24', dropdownContainerStyles) : 'w-1/2'}
            classes={
              view === 'form'
                ? tagsAndUserAutocompleteClasses
                : { popper: '!max-h-256', listbox: '!max-h-256' }
            }
            reviewerConfig={reviewerConfig}
            showError={showError}
            type={type}
            onReviewerConfigChange={onReviewerConfigChange}
          />
        ) : null}
      </Stack>
      {showDialog && (
        <SelectReviewersDialog
          reviewerConfig={reviewerConfig}
          setReviewers={onReviewerConfigChange}
          onClose={toggle}
        />
      )}
    </>
  );
}

export default SelectReviewers;
